import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { UiModes, UiPosition } from 'libs/rendering/gltf/config';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useContextManager } from 'apps/context/hooks';
import { useChatControl } from 'libs/rendering/hooks';
import { useClientStore } from 'apps/hooks';
import { TargetFaceIcon, TargetVertexIcon, EdgeIcon, FacesIcon } from 'icons';
import { Icon, Box } from 'components';

import { OldFrameSidebarWidth } from 'styles/constants';
import { isMobile } from 'styles/utils';

import {
  getSurfaceLabel,
  getSurfaceExtraLabels,
  getCurveLabel,
  getCurveExtraLabels,
} from 'apps/rfq/dfmConfig';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { getInstancePublicName } from 'libs/rendering/gltf/components/tree/treeNode';

import { DiffStatus, getDiffStatusColor } from 'libs/rendering/gltf/diffConfig';

import {
  InfoSectionStyled,
  NavigationList,
  HoverList,
  InfoItem,
  HoverItem,
  DebugInfoItem,
  SupScript,
} from './infoSection.styled';

const desktopMode = isInDesktopMode();

const InfoSection = () => {
  const [isInfoVisible, setInfoVisible] = useState(true);

  const viewerControl = useViewerControl();
  const clientStore = useClientStore();
  const contextManager = useContextManager();
  const chatControl = useChatControl();

  const { settings, selectionManager } = viewerControl.viewer;
  const viewer = viewerControl?.viewer;
  const currCamera = settings?.CurrentCamera();

  const { assembly } = viewerControl.cadFile || {};
  const { diffTool, isDiffing } = viewer || {};

  const handleInfoClick = () => {
    setInfoVisible(prevState => !prevState);
  };

  const rightOffset = () => {
    if (
      contextManager &&
      !viewerControl?.isPreview &&
      chatControl?.chatVisible
    ) {
      return UiPosition.panel;
    }
    return 0;
  };

  if (!currCamera) return null;

  const lastHighlightDebug =
    viewerControl.debugFeatures && selectionManager.HighlightCount() === 1
      ? selectionManager.GetLastHighlight()
      : null;

  const infoElementBuilder = () => {
    if (isDiffing) return null;
    if (!viewerControl.cadReady) return null;
    if (isMobile()) {
      if (viewerControl.getLeftOffset() > 0 || rightOffset() > 0) return null;
    }

    const tabLoadingStatus = currCamera?.GetLoadStatus();
    if (!currCamera || tabLoadingStatus !== 0) return null;

    let unitLabel = viewerControl.cadFile?.unitLabel;
    if (currCamera.IsInstance()) {
      // single part: check if BREP or MESH
      const instance = currCamera?.GetInstance();
      const body = instance?.GetBody();
      if (body) {
        const hasSeparation = body.HasSeparation();
        const brep = body.GetBrep();
        const mesh = body.GetBodyMesh();
        let type = 'UNKNOWN';
        if (brep) type = 'BREP';
        else if (mesh) {
          if (hasSeparation) type = 'Faceted mesh body';
          else {
            type = 'Mesh';
            unitLabel = null;
          }
        }

        const vol = body.GetVolume();
        const surf = body.GetSurface();
        return (
          <NavigationList visible={isInfoVisible}>
            {!isMobile() && (
              <InfoItem onClick={handleInfoClick} collapsed={!isInfoVisible}>
                <Icon name='info' size='small' />
              </InfoItem>
            )}
            <InfoItem>{type}</InfoItem>
            {vol > 0 && (
              <InfoItem>
                {`${vol.toExponential(2)} ${unitLabel || ''}`}
                <SupScript>3</SupScript>
              </InfoItem>
            )}
            {surf > 0 && (
              <InfoItem>
                {`${surf.toExponential(2)} ${unitLabel || ''}`}
                <SupScript>2</SupScript>
              </InfoItem>
            )}
            {!unitLabel && <InfoItem lineThrough>units</InfoItem>}

            {/* mouse picking debug info */}
            {lastHighlightDebug && (
              <DebugInfoItem>
                {lastHighlightDebug.IsFace()
                  ? `face ${lastHighlightDebug.faceHitIx} [${
                      3 * lastHighlightDebug.triangleHitIx
                    }]`
                  : `facet ${lastHighlightDebug.triangleHitIx}`}
              </DebugInfoItem>
            )}
          </NavigationList>
        );
      }
    } else if (assembly && currCamera.IsAssembly()) {
      // assembly...
      const partCount = assembly.bodies.size(); // what we call "part" is a "body" in PRC standard
      const instanceCount = assembly.InstanceCount();
      return (
        <NavigationList visible={isInfoVisible}>
          {!isMobile() && (
            <InfoItem onClick={handleInfoClick} collapsed={!isInfoVisible}>
              <Icon name='info' size='small' />
            </InfoItem>
          )}
          <InfoItem>{'Assembly'}</InfoItem>
          <InfoItem>{`${partCount} Parts`}</InfoItem>
          <InfoItem>{`${instanceCount} Instances`}</InfoItem>
        </NavigationList>
      );
    } else if (currCamera.IsSubAssembly()) {
      const prod = currCamera.GetSubAssembly();
      if (prod) {
        const instanceCount = prod.GetInstancesBelow().size();
        const partCount = prod.GetInnerBodyCount();
        return (
          <NavigationList visible={isInfoVisible}>
            {!isMobile() && (
              <InfoItem onClick={handleInfoClick} collapsed={!isInfoVisible}>
                <Icon name='info' size='small' />
              </InfoItem>
            )}
            <InfoItem>{'Sub-assembly'}</InfoItem>
            <InfoItem>{`${partCount} Parts`}</InfoItem>
            <InfoItem>{`${instanceCount} instances`}</InfoItem>
          </NavigationList>
        );
      }
    } else if (currCamera.IsDrawing()) {
      const drawing = currCamera.GetDrawing();
      if (drawing) {
        const sheetIx = drawing.currentSheetIx;
        const nSheets = drawing.SheetCount();
        const sheet = drawing.GetCurrentSheet();
        const scale = sheet.GetScale();
        return (
          <NavigationList>
            <InfoItem>
              <Icon name='info' size='small' />
            </InfoItem>
            <InfoItem>{'Drawing'}</InfoItem>
            <InfoItem>{`Sheet ${sheetIx + 1} of ${nSheets}`}</InfoItem>
            {sheet?.GetName().length > 0 && (
              <InfoItem>{sheet.GetName()}</InfoItem>
            )}
            {scale !== 0 && <InfoItem>{`Scale: ${scale.toFixed(3)}`}</InfoItem>}
          </NavigationList>
        );
      }
    }

    return null;
  };

  const currHoverInfo = () => {
    if (!viewerControl.cadReady) return null;
    const tabLoadingStatus = currCamera?.GetLoadStatus();
    if (!currCamera || tabLoadingStatus !== 0) return null;

    // NOTE: we show all in mm.
    let unitLabel = 'mm'; // viewerControl.cadFile?.unitLabel;

    let hoverLabel = null;
    let nodeIcon = null;

    const hoverItem = viewerControl.hoveredTarget;

    if (!hoverItem) {
      if (
        selectionManager.HighlightCount() > 1 &&
        viewerControl.uiMode === UiModes.assembly
      ) {
        // sub-assembly (highlighted from tree)
        const prod = selectionManager.GetHoveredProd();
        if (prod) {
          hoverLabel = prod.name;
          nodeIcon = <Icon name='assembly' size='medium' />;

          const instancesBelow = prod.GetInstancesBelow()?.size();
          const nInstances = prod.GetInstanceCount();
          const nProd = prod.GetSubAssemblyCount();

          if (nInstances > 0)
            // eslint-disable-next-line prettier/prettier
            hoverLabel += `⋮${nInstances} instance${
              nInstances > 1 ? 's' : ''
              // eslint-disable-next-line prettier/prettier
            }`;

          if (nProd > 0)
            // eslint-disable-next-line prettier/prettier
            hoverLabel += `⋮${nProd} sub-assembl${
              nProd === 1 ? 'y' : 'ies'
              // eslint-disable-next-line prettier/prettier
            }`;

          const rem = instancesBelow - nInstances;
          if (rem > 0)
            // eslint-disable-next-line prettier/prettier
            hoverLabel += ` (+${rem} instance${
              rem > 1 ? 's' : ''
              // eslint-disable-next-line prettier/prettier
            })`;
        }
      }
    } else {
      if (!hoverItem) return null;

      if (currCamera.IsInstance()) {
        const instance = currCamera.GetInstance();
        let body = instance?.GetBody();
        if (!body) return null;

        if (isDiffing) {
          if (hoverItem.cadFileId !== diffTool?.refId) {
            if (hoverItem.cadFileId !== diffTool?.diffId) return null;
            body = diffTool.GetDiffBody();
          }
        }

        const brep = body?.GetBrep();
        if (!brep) unitLabel = 'u';

        // get sclae factor to show in output scale (by now always mm)
        const unitScale = body?.GetScale();

        const wasmObj = body?.isFaceted
          ? body?.GetBodyMesh()?.GetMeshFrame()
          : body?.GetBrep();

        if (wasmObj && wasmObj.faceCount > 0) {
          let face = wasmObj.GetFace(hoverItem.faceHitIx);
          if (!face) face = wasmObj.GetFace(hoverItem.topoIndex);
          if (face) {
            if (hoverItem.IsFace()) {
              nodeIcon = <FacesIcon />;
              const surfEnum = viewerControl?.viewer?.module.SurfaceType;
              const surfJson = JSON.parse(face?.GetSurfaceData());
              const faceLabel = getSurfaceLabel(surfJson.type, surfEnum);

              hoverLabel = faceLabel;
              const extraLabels = getSurfaceExtraLabels(
                surfJson,
                surfEnum,
                unitScale,
              );
              if (extraLabels && extraLabels.length > 0) {
                hoverLabel += '⋮';
                for (let i = 0; i < extraLabels.length; i++) {
                  if (i > 0) hoverLabel += '⋮';
                  hoverLabel += `${extraLabels[i].txt}${extraLabels[i].val}`;
                  if (!extraLabels[i].isAngle) {
                    hoverLabel += `${unitLabel}`;
                  }
                  hoverLabel += ' ';
                }
              }
            } else if (hoverItem.IsLine()) {
              const loop = face.Loop(hoverItem.loopHitIx);
              if (loop) {
                const edge = loop.Edge(hoverItem.edgeHitIx);
                if (edge) {
                  nodeIcon = <EdgeIcon />;
                  const crvEnum = viewerControl?.viewer?.module.CurveType;
                  const crvJson = JSON.parse(edge?.GetCurveData());
                  const edgeLabel = getCurveLabel(crvJson.type, crvEnum);
                  hoverLabel = edgeLabel;
                  const extraLabels = getCurveExtraLabels(
                    crvJson,
                    crvEnum,
                    unitScale,
                  );
                  if (extraLabels && extraLabels.length > 0) {
                    hoverLabel += '⋮';
                    for (let i = 0; i < extraLabels.length; i++) {
                      if (i > 0) hoverLabel += '⋮';
                      hoverLabel += `${extraLabels[i].txt}${extraLabels[i].val} `;
                    }
                    hoverLabel += `${unitLabel}`;
                  }
                }
              }
            } else if (hoverItem.IsVertexPoint()) {
              nodeIcon = <TargetVertexIcon />;
              const pt = hoverItem.GetPt();
              hoverLabel = `[${pt.x.toFixed(2)}, ${pt.y.toFixed(
                2,
              )}, ${pt.z.toFixed(2)}]`;
            } else if (hoverItem.IsPoint()) {
              nodeIcon = <TargetFaceIcon />;
              // "sticky" point
              const pt = hoverItem.GetPt();
              hoverLabel = `[${pt.x.toFixed(2)}, ${pt.y.toFixed(
                2,
              )}, ${pt.z.toFixed(2)}]`;
            }
          }
        } else {
          // MESH
          // eslint-disable-next-line no-lonely-if
          if (hoverItem.IsTriangle()) {
            nodeIcon = <TargetFaceIcon />;
            const pt = hoverItem.GetPt();
            hoverLabel = `[${pt.x.toFixed(2)}, ${pt.y.toFixed(
              2,
            )}, ${pt.z.toFixed(2)}]`;
          } else if (hoverItem.IsPoint()) {
            // mesh point
            nodeIcon = <TargetVertexIcon />;
            const pt = hoverItem.GetPt();
            hoverLabel = `[${pt.x.toFixed(2)}, ${pt.y.toFixed(
              2,
            )}, ${pt.z.toFixed(2)}]`;
          }
        }
      }
      if (currCamera.IsAssembly() || currCamera.IsSubAssembly()) {
        nodeIcon = <Icon name='assembly' size='medium' />;
        const instance = assembly.GetInstance(hoverItem.instanceHitIx);
        if (instance) {
          hoverLabel = getInstancePublicName(instance);
          const body = instance?.GetBody();
          if (body) {
            if (body.isFaceted) {
              nodeIcon = <Icon name='mesh' size='small' viewBox='0 0 40 40' />;
            } else {
              nodeIcon = <Icon name='brep' size='small' viewBox='0 0 40 40' />;
            }
          }
        }
      }
    }
    if (!hoverLabel) return null;

    return (
      <HoverList>
        <Box sx={{ display: 'inline-block' }}>{nodeIcon}</Box>
        <HoverItem>{hoverLabel}</HoverItem>
      </HoverList>
    );
  };

  const diffInfoElementBuilder = () => {
    if (!isDiffing) return null;
    if (!viewerControl.cadReady) return null;
    if (isMobile()) {
      if (viewerControl.getLeftOffset() > 0 || rightOffset() > 0) return null;
    }

    const camera = settings?.CurrentCamera();
    const tabLoadingStatus = camera?.GetLoadStatus();
    if (!camera || tabLoadingStatus !== 0) return null;

    const refFile = viewerControl.file;
    const { diffFile } = viewerControl;
    if (!refFile || !diffFile) return null;

    const refName = `${refFile.filename} (${desktopMode ? 'cloud' : 'base'})`;
    const diffName = `${diffFile.filename} (${desktopMode ? 'local' : 'edit'})`;

    const isMeshDiff = viewer?.diffTool?.isMeshDiff;
    let result = null;
    if (viewer?.diffTool?.hasResult) {
      if (!isMeshDiff) {
        const resObj = viewer?.diffTool?.GetResult();
        result = {
          unchanged: resObj.unchanged.size(),
          added: resObj.added.size(),
          deleted: resObj.deleted.size(),
          transformed: resObj.transformed.size(),
        };

        if (!viewer?.diffTool?.highlightMergeSplit) {
          result.added += viewer?.diffTool?.splitAddCount;
          result.deleted += viewer?.diffTool?.mergeDeleteCount;
        }
      } else {
        // mesh
        const resObj = viewer?.diffTool?.GetMeshResult();
        result = {
          // added / deleted mesh points
          added: resObj.added.size(),
          deleted: resObj.deleted.size(),
        };
      }
    }

    return (
      <NavigationList visible={isInfoVisible}>
        {!isMobile() && (
          <InfoItem onClick={handleInfoClick} collapsed={!isInfoVisible}>
            <Icon name='info' size='small' />
          </InfoItem>
        )}
        <InfoItem>{refName}</InfoItem>
        <InfoItem>⟷</InfoItem>
        <InfoItem>{diffName}</InfoItem>

        {!viewer?.diffTool?.hasResult && <InfoItem>🔁 diffing...</InfoItem>}

        {result?.deleted > 0 && (
          <InfoItem
            dark
            background={getDiffStatusColor(DiffStatus.delete)}
          >{`${DiffStatus.delete} ${result?.deleted}`}</InfoItem>
        )}
        {result?.added > 0 && (
          <InfoItem
            dark
            background={getDiffStatusColor(DiffStatus.add)}
          >{`${DiffStatus.add} ${result?.added}`}</InfoItem>
        )}

        {(viewer?.diffTool?.isAssemblyDiff ||
          viewer?.diffTool?.highlightEdit) &&
          viewer?.diffTool?.editCount > 0 && (
            <InfoItem
              dark
              background={getDiffStatusColor(DiffStatus.edit)}
            >{`${DiffStatus.edit} ${viewer?.diffTool?.editCount}`}</InfoItem>
          )}

        {!isMeshDiff &&
          viewer?.diffTool?.highlightMergeSplit &&
          viewer?.diffTool?.splitAddCount + viewer?.diffTool?.mergeDeleteCount >
            0 && (
            <InfoItem
              dark
              background={getDiffStatusColor(DiffStatus.mergeSplit)}
            >{`+ ${viewer?.diffTool?.splitAddCount} | - ${viewer?.diffTool?.mergeDeleteCount}`}</InfoItem>
          )}

        {/* mouse picking debug info */}
        {lastHighlightDebug && (
          <DebugInfoItem>
            {lastHighlightDebug.IsFace()
              ? `face ${lastHighlightDebug.faceHitIx} [${
                  3 * lastHighlightDebug.triangleHitIx
                }]`
              : `facet ${lastHighlightDebug.triangleHitIx}`}
          </DebugInfoItem>
        )}
      </NavigationList>
    );
  };

  return (
    <InfoSectionStyled
      left={
        isMobile() || !clientStore.signedInProfile ? 0 : OldFrameSidebarWidth
      }
    >
      {!isDiffing ? infoElementBuilder() : diffInfoElementBuilder()}
      {currHoverInfo()}
    </InfoSectionStyled>
  );
};

export default observer(InfoSection);
